/**
 * The one place the mobile/desktop boundary is named, for both this panel and `UserManager`.
 *
 * ## The boundary is a raw media query, not an `sx` breakpoint key — and it has to be
 *
 * The reviewer set the switch to single-pane at **width <= 758px** (2026-09-24). Interstate's
 * effective MUI theme defines `xs: 0, sm: 479, md: 679, lg: 959, xl: 1167` and **nothing near 758**,
 * so this cannot be expressed as `{ md: … }`.
 *
 * ⚠️ **A numeric key does NOT work — this was checked, not assumed.** `@mui/system`'s
 * `handleBreakpoints` treats a key as a breakpoint only if it appears in `theme.breakpoints.values`;
 * anything else falls through to `acc[cssKey] = propValue[cssKey]` and is emitted as a **literal CSS
 * key**, so `{ 759: "row" }` silently produces invalid CSS. A value-level `"@media …"` key fails the
 * same way, because that branch assigns the raw value without wrapping it in the property.
 *
 * Hence {@link belowMobile}: the overrides are grouped into one **style-level** media query, which
 * is the only form that works. It reads better than the scattered per-property helper it replaced,
 * at the cost of the base value and its override no longer sitting on the same line.
 *
 * ## Desktop is the BASE; mobile is the override
 *
 * Deliberate, and it decides what **jsdom** sees. jsdom evaluates no media queries, so it resolves
 * the base — which means every Jest test renders the **desktop** arrangement and mobile behaviour
 * belongs in `UserManager.cy.tsx`, where a real viewport resolves the query. Flipping this to
 * mobile-first would silently change which shape hundreds of existing assertions are written
 * against.
 *
 * ## Prefer these over `useWindowDimensions` for anything that affects layout
 *
 * `useWindowDimensions` initialises to all-`false` with `currentSize: "xl"` and only corrects in an
 * effect, so its first paint reports "everything is narrow" on a desktop viewport — a visible flash
 * of the mobile arrangement on every load. These objects are pure CSS and are therefore correct on
 * the first frame. Reserve the hook for behaviour that genuinely cannot be expressed in CSS.
 *
 * Structural choices that *are* JavaScript here — which pane the drill-down shows — derive from the
 * **router**, not from a measured width, so they are correct on the first frame too.
 */

/**
 * The widest viewport that still gets the single-pane drill-down: **758px inclusive**.
 *
 * Reviewer call, 2026-09-24, replacing the `md` token (679px) this originally used. 759px and up is
 * the two-pane layout.
 */
export const MOBILE_MAX_PX = 758;

/** The media query every mobile override goes inside. */
const BELOW_MOBILE = `@media (max-width:${MOBILE_MAX_PX}px)`;

/**
 * Groups mobile overrides into the one media query that expresses the boundary.
 *
 * ```ts
 * sx={{ flexDirection: "row", ...belowMobile({ flexDirection: "column" }) }}
 * ```
 *
 * The base value is the **desktop** one; anything passed here overrides it at 758px and below.
 *
 * ## ⚠️ ONE of these per `sx` object — a second one silently wins
 *
 * Every helper here emits the **same** media-query key, so spreading two of them into one object
 * makes the later overwrite the earlier's *entire* block rather than merging with it:
 *
 * ```ts
 * // 🚫 loses `display: "flex"` — no error, no warning, the control just never appears
 * sx={{ ...mobileOnly("flex"), ...belowMobile({ marginLeft: "-8px" }) }}
 *
 * // ✅ one block
 * sx={mobileOnly("flex", { marginLeft: "-8px" })}
 * ```
 *
 * This bit twice on 2026-09-24 — the back affordance vanished at 390px, and the identity name lost
 * its `nowrap`. Both were caught by Cypress rather than by review, because the shape of the code
 * looks completely reasonable. **The same trap applies to a spread constant that was itself built
 * from one of these helpers**, which is why `IDENTITY_NAME_*` and `IDENTITY_META_*` in
 * `IdentityHeader` are split into a base object and a separate overrides object rather than a single
 * pre-wrapped one.
 */
export const belowMobile = (styles: Record<string, unknown>) => ({
  [BELOW_MOBILE]: styles
});

/**
 * Hidden at 758px and below, shown above it. Pass the desktop `display` value.
 *
 * `extra` carries any *other* mobile overrides that belong with it — use it rather than spreading a
 * second {@link belowMobile}, for the reason documented there.
 */
export const desktopOnly = (
  display = "block",
  extra: Record<string, unknown> = {}
) => ({
  display,
  [BELOW_MOBILE]: { display: "none", ...extra }
});

/** Shown at 758px and below, hidden above it. See {@link desktopOnly} for `extra`. */
export const mobileOnly = (
  display = "block",
  extra: Record<string, unknown> = {}
) => ({
  display: "none",
  [BELOW_MOBILE]: { display, ...extra }
});

/**
 * Whether the viewport is currently in the single-pane band.
 *
 * ⚠️ **Call this from an event handler, never during render.** Reading the viewport during render is
 * what makes a responsive component flash the wrong layout on its first paint, and it is why the
 * layout here is CSS rather than JavaScript. There is exactly one thing CSS cannot do — decide
 * whether a click should *push a history entry* — and that happens in a click handler, where the
 * viewport is settled and no render depends on the answer.
 *
 * Shares {@link MOBILE_MAX_PX} with the CSS above, so the two cannot disagree about where the
 * layout switches — a class of bug that only shows up in the gap between two thresholds.
 *
 * In jsdom `window.matchMedia` is mocked to always report `matches: false` (repo-root
 * `setupTests.ts`), so this reads **desktop** in every Jest test unless a spec overrides the mock.
 */
export const isMobileViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(`(max-width:${MOBILE_MAX_PX}px)`).matches;

export const ACTIONS_COLLAPSE_PX = 1360;

const BELOW_COLLAPSE = `@media (max-width:${ACTIONS_COLLAPSE_PX - 0.05}px)`;

/**
 * Shown only at or above {@link ACTIONS_COLLAPSE_PX} — the labelled action buttons.
 *
 * Cannot reuse {@link desktopOnly}, which keys off the `md` breakpoint; these two thresholds are
 * independent and both are in play between 680px and 1360px.
 */
export const labelledActionsOnly = (display = "block") => ({
  display,
  [BELOW_COLLAPSE]: { display: "none" }
});

/** Shown only below {@link ACTIONS_COLLAPSE_PX} — the `⋮` trigger. See {@link labelledActionsOnly}. */
export const collapsedActionsOnly = (display = "block") => ({
  display: "none",
  [BELOW_COLLAPSE]: { display }
});
